import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';

import api from 'services/api';

import { addNotification } from 'actions/globalActions';
import {
  BLOCK_THRESHOLD,
  CHALLENGE_THRESHOLD,
  FLAGS,
  SCORE_MAX,
  SCORE_MIN,
  WEIGHTS,
} from 'constants/classifier';
import Label from 'components/common/Label';
import DropDown from 'components/common/Dropdown';
import Card from 'components/common/Card';
import Button from 'components/common/Button';
import Icon from 'components/common/Icon';

const scoreToPosition = (score) =>
  Math.min(
    100,
    Math.max(0, ((score - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100)
  );

const ClassifierCard = ({ requester = {}, request, groups = [] }) => {
  const classifier = request || requester;
  if (!classifier.score) {
    classifier.score = Object.keys(FLAGS).reduce((acc, flag) => {
      if ((classifier.flags & FLAGS[flag]) !== 0) {
        acc += WEIGHTS[flag];
      }
      return acc;
    }, 0);
  }

  const isRequest = Boolean(request);
  const currentLogger = useSelector((state) => state.global.currentLogger);
  const { requesterId: currentRequester } = useParams();
  const scorePosition = Number.isFinite(classifier.score)
    ? scoreToPosition(classifier.score)
    : 0;

  const [requesterGroup, setRequesterGroup] = useState({
    id: requester.groupId,
    name: requester.groupName,
  });

  const [overrideGroup, setOverrideGroup] = useState(requester.overrideGroup);

  const [requesterIsWhitelist, setRequesterIsWhitelist] = useState(
    requester.isWhitelist
  );
  const [requesterIsBlacklist, setRequesterIsBlacklist] = useState(
    requester.isBlacklist
  );

  const [groupFilter, setGroupFilter] = useState('');

  const [isGroupLoading, setIsGroupLoading] = useState(false);
  const [isWhitelistLoading, setIsWhitelistLoading] = useState(false);
  const [isBlacklistLoading, setIsBlacklistLoading] = useState(false);

  const dispatch = useDispatch();

  const updateGroup = (groupId) => {
    setIsGroupLoading(true);
    api.updateRequesterGroup(
      currentLogger,
      currentRequester,
      { groupId },
      (res) => {
        if (res.data.status === 200) {
          setRequesterGroup({
            ...res.data.group,
          });
          dispatch(
            addNotification({
              title: 'Requester group updated',
              type: 'success',
            })
          );
        } else {
          dispatch(
            addNotification({
              title: 'Cannot update requester group',
              description: 'Please refresh the page and try again',
              type: 'warning',
            })
          );
        }
        setIsGroupLoading(false);
      }
    );
  };

  const deleteGroup = () => {
    setIsGroupLoading(true);
    api.deleteRequesterGroup(currentLogger, currentRequester, (res) => {
      if (res.data.status === 200) {
        setRequesterGroup({
          ...res.data.group,
        });
        dispatch(
          addNotification({
            title: 'Requester group deleted',
            type: 'success',
          })
        );
      } else {
        dispatch(
          addNotification({
            title: 'Cannot delete requester group',
            description: 'Please refresh the page and try again',
            type: 'warning',
          })
        );
      }
      setIsGroupLoading(false);
    });
  };

  const handleOverrideGroupChange = () => {
    console.log('override group');

    api.updateRequesterOverride(
      currentLogger,
      currentRequester,
      {
        overrideGroup: !overrideGroup,
      },
      (res) => {
        if (res.data.status === 200) {
          const { overrideGroup } = res.data;
          setOverrideGroup(overrideGroup);
          dispatch(
            addNotification({
              title: 'Group override updated',
              type: 'success'
            })
          );
        } else {
          dispatch(
            addNotification({
              title: 'Cannot udpate override',
              description: 'Please refresh the page and try again',
              type: 'warning',
            })
          );
        }
      }
    );
  };

  const handleIsWhitelistChange = () => {
    setIsWhitelistLoading(true);
    api.updateRequesterFlags(
      currentLogger,
      currentRequester,
      {
        isWhitelist: !requesterIsWhitelist,
        isBlacklist: requesterIsBlacklist,
      },
      (res) => {
        if (res.data.status === 200) {
          const { isWhitelist, isBlacklist } = res.data;
          setRequesterIsWhitelist(isWhitelist);
          setRequesterIsBlacklist(isBlacklist);
          dispatch(
            addNotification({
              title: 'Requester flags updated',
              type: 'success',
            })
          );
        } else {
          dispatch(
            addNotification({
              title: 'Cannot update requester flags',
              description: 'Please refresh the page and try again',
              type: 'warning',
            })
          );
        }
        setIsWhitelistLoading(false);
      }
    );
  };

  const handleIsBlacklistChange = () => {
    setIsBlacklistLoading(true);
    api.updateRequesterFlags(
      currentLogger,
      currentRequester,
      {
        isWhitelist: requesterIsWhitelist,
        isBlacklist: !requesterIsBlacklist,
      },
      (res) => {
        if (res.data.status === 200) {
          const { isWhitelist, isBlacklist } = res.data;
          setRequesterIsWhitelist(isWhitelist);
          setRequesterIsBlacklist(isBlacklist);
          dispatch(
            addNotification({
              title: 'Requester flags updated',
              type: 'success',
            })
          );
        } else {
          dispatch(
            addNotification({
              title: 'Cannot update requester flags',
              description: 'Please refresh the page and try again',
              type: 'warning',
            })
          );
        }
        setIsBlacklistLoading(false);
      }
    );
  };

  /**
   *
   * Creates group options for dropdown menu
   */
  const getGroupOptions = () =>
    groups
      .filter(({ name }) =>
        name.toLowerCase().startsWith(groupFilter.toLowerCase())
      )
      .map(({ id, name }) => (
        <a
          href="#"
          className="dropdown-item has-text-left"
          key={id}
          onMouseDown={() => updateGroup(id)}
        >
          {name}
        </a>
      ));

  const handleGroupFilterChange = (e) => {
    const { value } = e.target;
    setGroupFilter(value);
  };

  return (
    <Card>
      {!isRequest && <div className="my-3">
        <h3 className="has-text-weight-bold has-text-light">Group</h3>
        <div>
          <Label
            color="grey"
            deletable={requesterGroup.name ? true : false}
            onDelete={deleteGroup}
          >
            {requesterGroup.name ? requesterGroup.name : 'No Group'}
          </Label>
          <DropDown loading={isGroupLoading} className="is-pulled-right">
            <div className="field px-2">
              <p className="control has-icons-left">
                <input
                  className="input is-small"
                  type="text"
                  placeholder="Search groups"
                  value={groupFilter}
                  onChange={handleGroupFilterChange}
                />
                <span className="icon is-small is-left">
                  <i className="fas fa-search"></i>
                </span>
              </p>
            </div>
            {getGroupOptions()}
          </DropDown>
          <Button
            type="text"
            onClick={handleOverrideGroupChange}
            className="pb-4 p-2 is-pulled-right"
            tooltipText="Override Group"
          >
            <Icon size="small" color={overrideGroup ? 'primary' : 'grey'} icon="fa-edit" />
          </Button>
        </div>
      </div>}
      <div className="my-3 is-flex">
        <h3 className="has-text-weight-bold has-text-light mr-2">Action</h3>
        <code className="" style={{ width: '90px', textAlign: 'center' }}>{classifier.action || 'N/A'}</code>
        {classifier.decidedBy && (
          <span className=" ml-2 is-flex-shrink-0 has-tooltip-right" data-tooltip={classifier.decidedBy}>
            <Icon size="small" color="grey" icon="fa-info-circle" />
          </span>
        )}
      </div>
      <div className="mt-3">
        <h3 className="has-text-weight-bold has-text-light">Flags</h3>
        <div className="control is-flex is-justify-content-space-evenly">
          {!isRequest && <Button
            loading={isWhitelistLoading}
            type="text"
            className="p-5"
            onClick={handleIsWhitelistChange}
          >
            <Icon
              size="large"
              color={requesterIsWhitelist ? 'primary' : 'grey'}
              icon={!isWhitelistLoading ? 'fa-check-circle' : ''}
            />
          </Button>}
          {!isRequest && <Button
            loading={isBlacklistLoading}
            type="text"
            className="p-5"
            onClick={handleIsBlacklistChange}
          >
            <Icon
              size="large"
              color={requesterIsBlacklist ? 'primary' : 'grey'}
              icon={!isBlacklistLoading ? 'fa-ban' : ''}
            />
          </Button>}
        </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              marginTop: '0.5rem',
            }}
          >
            {Object.keys(FLAGS).map((flag) => {
              if ((classifier.flags & FLAGS[flag]) !== 0) {
                const color = FLAGS[flag] >= 1 << 20 ? '#38b2ac' : '#e53e3e';
                return (
                  <span
                    key={flag}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.25rem 0.625rem',
                      borderRadius: '999px',
                      backgroundColor: '#f0f2f5',
                      color: color,
                      fontSize: '0.75rem',
                      lineHeight: 1.25,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {flag}
                  </span>
                );
              }
              return null;
            })}
          </div>
          <div className="mt-4">
            <div className="is-flex is-justify-content-space-between mb-1">
              <h3 className="has-text-weight-bold has-text-light">Score</h3>
              <span className="has-text-weight-bold">{classifier.score}</span>
            </div>
            <div
              role="img"
              style={{
                position: 'relative',
                height: '1.75rem',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: '1rem',
                  left: 0,
                  borderRadius: '999px',
                  background:
                    'linear-gradient(to right, #38a169, #ecc94b, #e53e3e)',
                }}
              />
              {[CHALLENGE_THRESHOLD, BLOCK_THRESHOLD].map((threshold) => (
                <div
                  key={threshold}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: `${scoreToPosition(threshold)}%`,
                    height: '0.75rem',
                    borderLeft: '2px solid #363636',
                    transform: 'translateX(-1px)',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '0.8rem',
                      left: 0,
                      color: '#363636',
                      fontSize: '0.7rem',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      transform: 'translateX(-50%)',
                    }}
                  >
                    {threshold === CHALLENGE_THRESHOLD ? 'Challenge' : 'Block'}
                  </span>
                </div>
              ))}
              <span
                style={{
                  position: 'absolute',
                  top: '0.375rem',
                  left: `${scorePosition}%`,
                  width: '1rem',
                  height: '1rem',
                  border: '2px solid white',
                  borderRadius: '50%',
                  backgroundColor: '#363636',
                  boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.25)',
                  transform: 'translate(-50%, -50%)',
                }}
              />
            </div>
          </div>
      </div>
    </Card>
  );
};

ClassifierCard.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      isBot: PropTypes.bool,
      isWhitelist: PropTypes.bool,
      isBlacklist: PropTypes.bool,
      action: PropTypes.string,
      decidedBy: PropTypes.string,
      flags: PropTypes.number,
      score: PropTypes.number,
    })
  ),
  requester: PropTypes.shape({
    id: PropTypes.string,
    isBot: PropTypes.bool,
    isWhitelist: PropTypes.bool,
    isBlacklist: PropTypes.bool,
    groupId: PropTypes.string,
    groupName: PropTypes.string,
    overrideGroup: PropTypes.bool,
    score: PropTypes.number,
  }),
  request: PropTypes.shape({
    action: PropTypes.string,
    decidedBy: PropTypes.string,
    flags: PropTypes.number,
    score: PropTypes.number,
  }),
};

export default ClassifierCard;
